import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Compass,
  Building2,
  Key,
  Blocks,
  ClipboardList,
  Recycle,
  ShieldCheck,
  LineChart,
  Eye,
  Layers,
  Handshake,
  Coins,
} from "lucide-react";
import { useDocumentSEO } from "@/hooks/useDocumentSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGES = [
  "https://images.pexels.com/photos/15577446/pexels-photo-15577446.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
  "https://images.pexels.com/photos/302769/pexels-photo-302769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
  "https://images.pexels.com/photos/374870/pexels-photo-374870.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800",
];

const FAALIYET_KARTLARI = [
  {
    idx: "01",
    icon: Compass,
    title: "Mimari Projeler",
    slug: "mimari-projeler",
    body: "Konsept geliştirme, mimari tasarım ve proje uygulama dokümantasyonu.",
  },
  {
    idx: "02",
    icon: Building2,
    title: "İnşaat Taahhüt",
    slug: "insaat-taahhut",
    body: "Kurumsal disiplin ve süreç yönetimiyle inşaat taahhüt hizmetleri.",
  },
  {
    idx: "03",
    icon: Key,
    title: "Anahtar Teslim Projeler",
    slug: "anahtar-teslim",
    body: "Konseptten teslime, tek noktadan yönetilen bütüncül uygulamalar.",
  },
  {
    idx: "04",
    icon: Blocks,
    title: "Gayrimenkul Geliştirme",
    slug: "gayrimenkul-gelistirme",
    body: "Değer üretimi odaklı gayrimenkul geliştirme yaklaşımı.",
  },
  {
    idx: "05",
    icon: ClipboardList,
    title: "Proje Yönetimi ve Danışmanlık",
    slug: "proje-yonetimi",
    body: "Süreç kurgusu, planlama ve kontrol odaklı proje danışmanlığı.",
  },
  {
    idx: "06",
    icon: Recycle,
    title: "Yapı Yenileme ve Dönüşüm",
    slug: "yapi-yenileme",
    body: "Mevcut yapıların yenilenmesi ve kentsel dönüşüm projeleri.",
  },
];

const SURECLER = [
  { n: "01", title: "İhtiyaç Analizi", body: "Kapsam, hedef ve beklentilerin netleştirilmesi." },
  { n: "02", title: "Mimari Planlama", body: "Konsept ve tasarım kararlarının şekillendirilmesi." },
  { n: "03", title: "Bütçe ve Süreç Yönetimi", body: "Bütçe, program ve kaynak planlaması." },
  { n: "04", title: "Uygulama ve Koordinasyon", body: "Saha koordinasyonu ve kalite kontrolü." },
  { n: "05", title: "Teslim ve Değer Yönetimi", body: "Teslim sonrası değer sürdürülebilirliği." },
];

const NEDENLER = [
  {
    icon: ShieldCheck,
    title: "Kurumsal İş Yapma Disiplini",
    body: "1001 Grup'un ticari kültür ve iş etiğiyle yönetilen süreçler.",
  },
  {
    icon: LineChart,
    title: "Planlı Proje Yönetimi",
    body: "Kapsam, bütçe ve zaman planı üzerine kurgulanmış disiplinli yönetim.",
  },
  {
    icon: Eye,
    title: "Şeffaf Süreç Takibi",
    body: "Karar, ilerleme ve maliyetlerde izlenebilir raporlama anlayışı.",
  },
  {
    icon: Layers,
    title: "Mimari ve Ticari Bakışın Birleşimi",
    body: "Tasarım hassasiyeti ile yatırım perspektifini bir arada gözetiriz.",
  },
  {
    icon: Handshake,
    title: "Güvenilir Çözüm Ortakları",
    body: "Uzmanlık alanında referansı olan ekip ve tedarikçilerle çalışma.",
  },
  {
    icon: Coins,
    title: "Değer Odaklı Yatırım Yaklaşımı",
    body: "Kısa vadeli çıktı yerine sürdürülebilir değer önceliklendirmesi.",
  },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

function HeroSlider({ images, intervalMs = 6000 }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % images.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [images.length, intervalMs]);

  return (
    <>
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ease-in-out duration-[1600ms] ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </>
  );
}


export default function HomePage() {
  useDocumentSEO({
    title: "Ana Sayfa",
    description:
      "1001 Grup iştiraki 1001 Mimarlık İnşaat ve Ticaret A.Ş.; mimarlık, inşaat taahhüt, anahtar teslim proje ve gayrimenkul geliştirme alanlarında planlı ve değer odaklı çözümler sunar.",
  });
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Ad Soyad zorunludur.";
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Geçerli bir e-posta giriniz.";
    if (!form.message || form.message.trim().length < 5) e.message = "Kısa bir mesaj yazınız.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitQuick = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        subject: "Ana Sayfa Hızlı İletişim",
        kvkk_consent: true,
      });
      toast.success("Mesajınız iletildi. En kısa sürede dönüş yapacağız.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Mesaj gönderilemedi. Lütfen tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full bg-transparent border-0 border-b border-navy/20 focus:border-gold focus:outline-none py-3 text-[14px] placeholder:text-anthracite/40 text-navy transition-colors";

  return (
    <>
      {/* 1. HERO */}
      <section
        data-testid="home-hero"
        className="relative bg-navy text-white overflow-hidden"
      >
        <div className="absolute inset-0">
          <HeroSlider images={HERO_IMAGES} />
          <div className="absolute inset-0 bg-gradient-to-br from-navy/95 via-navy/85 to-navy/75" />
          <div className="absolute inset-0 blueprint-grid opacity-30" />
          {/* subtle diagonal architectural line */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            preserveAspectRatio="none"
            viewBox="0 0 1400 900"
            aria-hidden
          >
            <line x1="0" y1="700" x2="1400" y2="200" stroke="rgba(197,160,89,0.35)" strokeWidth="1" />
            <line x1="0" y1="750" x2="1400" y2="250" stroke="rgba(197,160,89,0.15)" strokeWidth="1" />
            <circle cx="1050" cy="410" r="3" fill="#C5A059" />
            <circle cx="1050" cy="410" r="18" fill="none" stroke="rgba(197,160,89,0.5)" strokeWidth="1" />
          </svg>
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-24 pb-24 lg:pt-40 lg:pb-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-9 lg:col-span-8">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight leading-[1.02] animate-fade-up">
                Yaşam alanlarına
                <br />
                <span className="text-gold italic font-extralight">değer katan</span> yapılar.
              </h1>

              <p className="mt-8 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed font-light animate-fade-up">
                1001 Mimarlık İnşaat ve Ticaret A.Ş. olarak mimari projelendirme,
                inşaat taahhüt, anahtar teslim uygulamalar ve gayrimenkul
                geliştirme alanlarında planlı, güvenilir ve değer odaklı
                çözümler sunuyoruz.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 animate-fade-up">
                <Link
                  to="/faaliyet-alanlari"
                  data-testid="hero-cta-faaliyet"
                  className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-hover text-white px-7 py-3.5 text-[12.5px] tracking-widest uppercase font-medium transition-colors"
                >
                  Faaliyet Alanlarımız
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/iletisim"
                  data-testid="hero-cta-iletisim"
                  className="group inline-flex items-center gap-3 border border-white/30 hover:border-gold text-white/85 hover:text-gold px-7 py-3.5 text-[12.5px] tracking-widest uppercase font-medium transition-colors"
                >
                  Bizimle İletişime Geçin
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            <div className="md:col-span-3 md:col-start-10 hidden md:flex flex-col justify-end">
              <div className="border-l border-gold/40 pl-4 space-y-6">
                <div>
                  <div className="overline text-white/40">Faaliyet</div>
                  <div className="mt-1 font-heading text-[15px] text-white/90">
                    Mimarlık &amp; İnşaat
                  </div>
                </div>
                <div>
                  <div className="overline text-white/40">Merkez</div>
                  <div className="mt-1 font-heading text-[15px] text-white/90">
                    İstanbul · TR
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 lg:mt-24 flex items-center gap-4 text-white/50 text-[11px] tracking-[0.3em] uppercase">
            <div className="w-16 h-[1px] bg-white/30" />
            Aşağı Kaydırın
          </div>
        </div>
      </section>

      {/* 2. KURUMSAL TANITIM */}
      <section
        data-testid="home-kurumsal"
        className="bg-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[1px] bg-gold" />
              <span className="overline text-anthracite/70">Kurumsal</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
              Kurumsal disiplinle
              <br />
              şekillenen,{" "}
              <span className="text-gold italic font-extralight">
                mimari vizyonla
              </span>
              <br />
              yükselen bir yapı anlayışı.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <div className="space-y-6 text-base md:text-lg text-anthracite/85 leading-relaxed font-light">
              <p>
                <span className="text-navy font-medium">
                  1001 Grup &amp; Yenilik Makina İnşaat iştiraki
                </span>{" "}
                olarak 2021 yılında hayata geçen 1001 Mimarlık İnşaat ve Ticaret
                A.Ş., yolculuğuna inşaat taahhüt işleriyle başlamış; bugün
                anahtar teslim proje geliştirme, mimari projelendirme ve
                gayrimenkul geliştirme alanlarına uzanan bütüncül bir yapı
                markasına dönüşmüştür.
              </p>
              <p>
                1001 Grup&apos;un kurumsal iş yapma kültürü ile{" "}
                <span className="text-navy font-medium">
                  Yenilik Makina İnşaat
                </span>
                &apos;ın sektörel saha tecrübesini bir araya getirerek; planlı
                ve güvenilir bir yaklaşımla kalıcı değer üretmeyi hedefler.
              </p>
            </div>
            <div className="mt-10 flex items-center gap-4">
              <Link
                to="/kurumsal"
                data-testid="home-kurumsal-link"
                className="group inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-[12.5px] tracking-widest uppercase font-medium"
              >
                <span className="w-8 h-[1px] bg-gold group-hover:w-12 transition-all" />
                Kurumsal Hakkında Daha Fazla Bilgi
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 3. FAALİYET ALANLARI — 6 kart */}
      <section
        data-testid="home-faaliyet"
        className="bg-anthracite text-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-gold">Faaliyet Alanlarımız</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                Altı farklı uzmanlık alanı,
                <br />
                <span className="text-gold italic font-extralight">
                  tek kurumsal duruşta
                </span>{" "}
                buluşuyor.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base leading-relaxed text-white/70 font-light max-w-lg">
                Mimari tasarımdan gayrimenkul geliştirmeye uzanan hizmet zincirimiz, projeleri bütüncül bir mimari
                bakışla ele alma anlayışımızın somut ifadesidir.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/10">
            {FAALIYET_KARTLARI.map(({ icon: Icon, title, slug, body }) => (
              <Link
                key={slug}
                to={`/faaliyet-alanlari#${slug}`}
                data-testid={`faaliyet-card-${slug}`}
                className="group relative bg-anthracite hover:bg-navy p-8 lg:p-10 flex flex-col justify-between min-h-[240px] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <Icon size={30} className="text-gold" strokeWidth={1.25} />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-medium mb-2 text-white">
                    {title}
                  </h3>
                  <p className="text-[13.5px] leading-relaxed text-white/70 font-light">
                    {body}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-gold text-[11px] tracking-[0.3em] uppercase">
                    <span className="w-8 h-[1px] bg-gold group-hover:w-12 transition-all" />
                    Detay
                    <ArrowUpRight
                      size={13}
                      className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. PROJE GELİŞTİRME YAKLAŞIMI — Timeline */}
      <section
        data-testid="home-surec"
        className="bg-white py-24 lg:py-32 relative"
      >
        <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-anthracite/70">Proje Yaklaşımımız</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
                Beş aşamalı
                <br />
                disiplinli süreç.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base leading-relaxed text-anthracite/80 font-light max-w-lg">
                Her projeyi ihtiyaç analizinden teslim sonrası değer yönetimine kadar planlı ve izlenebilir bir süreç
                olarak kurgularız.
              </p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* horizontal rule for desktop */}
            <div className="hidden lg:block absolute top-[26px] left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

            <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
              {SURECLER.map((s) => (
                <li key={s.n} className="relative">
                  <div className="flex items-center gap-3">
                    <span className="w-[52px] h-[52px] border border-navy/20 bg-white flex items-center justify-center relative z-10">
                      <span className="w-2 h-2 bg-gold" />
                    </span>
                    <span className="w-8 h-[1px] bg-gold lg:hidden" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-navy">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-anthracite/75 font-light max-w-[220px]">
                    {s.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-16">
            <Link
              to="/proje-yaklasimimiz"
              data-testid="home-surec-link"
              className="group inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-[12.5px] tracking-widest uppercase font-medium"
            >
              <span className="w-8 h-[1px] bg-gold group-hover:w-12 transition-all" />
              Proje Yaklaşımımızı İnceleyin
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* 5. NEDEN 1001 MİMARLIK İNŞAAT? */}
      <section
        data-testid="home-neden"
        className="bg-navy text-white py-24 lg:py-32 relative overflow-hidden"
      >
        <div className="absolute inset-0 blueprint-grid opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span className="overline text-gold">Neden Biz</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                Neden 1001
                <br />
                Mimarlık İnşaat?
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-base leading-relaxed text-white/75 font-light max-w-lg">
                Güven ilişkimizi tamamlanmış işlerin sayısı üzerinden değil; kurumsal disiplin, süreç ve
                yaklaşımımız üzerinden kuruyoruz.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-white/10">
            {NEDENLER.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                data-testid={`neden-item-${i}`}
                className="bg-navy p-8 lg:p-10 flex flex-col min-h-[220px]"
              >
                <div className="flex items-center gap-4">
                  <span className="w-10 h-10 border border-gold/50 flex items-center justify-center text-gold shrink-0">
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  <span className="w-8 h-[1px] bg-gold/50" />
                </div>
                <h3 className="mt-6 font-heading text-lg font-medium">{title}</h3>
                <p className="mt-3 text-[13.5px] leading-relaxed text-white/70 font-light">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. CTA — Teklif / İş Birliği */}
      <section
        data-testid="home-cta-band"
        className="bg-anthracite text-white py-20 lg:py-24 relative"
      >
        <div className="h-[1px] hairline-x absolute top-0 left-0 right-0" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-gold mb-4">İş Birliği</div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Projenizi birlikte planlayalım.
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed font-light">
              Fikir aşamasındaki bir projeniz ya da değerlendirilecek bir iş birliği talebiniz varsa, ekibimizle
              bağlantı kurabilirsiniz.
            </p>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link
              to="/iletisim"
              data-testid="home-cta-teklif"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-hover text-white px-8 py-4 text-[12.5px] tracking-widest uppercase font-medium transition-colors"
            >
              İş Birliği Talebi Oluşturun
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* 7. KISA İLETİŞİM FORMU */}
      <section
        data-testid="home-quick-contact"
        className="bg-white py-24 lg:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-[1px] bg-gold" />
              <span className="overline text-anthracite/70">Hızlı İletişim</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-navy leading-tight">
              Kısa bir not
              <br />
              bırakmanız yeterli.
            </h2>
            <p className="mt-6 text-anthracite/80 leading-relaxed font-light max-w-md">
              Detaylı bilgi için{" "}
              <Link to="/iletisim" className="text-navy underline underline-offset-2 hover:text-gold">
                İletişim
              </Link>{" "}
              sayfamızdaki geniş formu kullanabilirsiniz. Buradan hızlı bir mesaj bırakmanız da yeterli olacaktır.
            </p>

            <div className="mt-10 h-[1px] bg-navy/10" />
            <div className="mt-6 flex items-center gap-3 text-[12px] tracking-[0.2em] uppercase text-anthracite/60">
              <span className="w-6 h-[1px] bg-gold" />
              KVKK aydınlatma metni kapsamında değerlendirilir.
            </div>
          </div>

          <div className="md:col-span-7">
            <form
              onSubmit={submitQuick}
              data-testid="home-quick-contact-form"
              className="border border-navy/10 p-8 lg:p-10 bg-white"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="overline text-anthracite/60 block mb-2" htmlFor="quick-name">
                    Ad Soyad *
                  </label>
                  <input
                    id="quick-name"
                    type="text"
                    autoComplete="name"
                    className={inputBase}
                    placeholder="Adınız Soyadınız"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="home-quick-input-name"
                  />
                  {errors.name && (
                    <p className="mt-2 text-[12px] text-red-600" data-testid="home-error-name">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="overline text-anthracite/60 block mb-2" htmlFor="quick-phone">
                    Telefon
                  </label>
                  <input
                    id="quick-phone"
                    type="tel"
                    autoComplete="tel"
                    className={inputBase}
                    placeholder="+90 ..."
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="home-quick-input-phone"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="overline text-anthracite/60 block mb-2" htmlFor="quick-email">
                    E-Posta *
                  </label>
                  <input
                    id="quick-email"
                    type="email"
                    autoComplete="email"
                    className={inputBase}
                    placeholder="ornek@sirket.com"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="home-quick-input-email"
                  />
                  {errors.email && (
                    <p className="mt-2 text-[12px] text-red-600" data-testid="home-error-email">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="overline text-anthracite/60 block mb-2" htmlFor="quick-message">
                    Mesajınız *
                  </label>
                  <textarea
                    id="quick-message"
                    rows={4}
                    className={`${inputBase} resize-none`}
                    placeholder="Kısa bir mesaj bırakın..."
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    data-testid="home-quick-input-message"
                  />
                  {errors.message && (
                    <p className="mt-2 text-[12px] text-red-600" data-testid="home-error-message">
                      {errors.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="home-quick-submit"
                  className="group inline-flex items-center gap-3 bg-navy hover:bg-gold text-white px-8 py-3.5 text-[12.5px] tracking-widest uppercase font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Gönderiliyor..." : "Gönder"}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
                <div className="text-[11px] tracking-[0.2em] uppercase text-anthracite/50">
                  * Zorunlu alanlar
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
